import React, { useEffect, useState, FC } from 'react'
import { Modal, Button } from 'antd'
import { hot } from 'react-hot-loader/root'
import { observer } from 'mobx-react'
import { ModalProps, ModalFuncProps } from 'antd/es/modal'
import { ModalStaticFunctions, ModalFunc } from 'antd/es/modal/confirm'
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import DDButton from 'src/pc/components/DDButton'
import DDGhostBtn from 'src/pc/components/DDGhostBtn'
import styled from '@emotion/styled'
import classNames from 'classnames'

type THandleOpenModal = (
  props: ModalFuncProps,
  type: keyof typeof iconMapping,
) => {
  destroy: () => void
  update: (newConfig: ModalFuncProps) => void
}

const iconMapping = {
  success: <CheckCircleOutlined />,
  warn: <ExclamationCircleOutlined />,
  info: <InfoCircleOutlined />,
  error: <CloseCircleOutlined />,
  confirm: undefined,
}

const handleOpenModal: THandleOpenModal = (props, type) => {
  const hasIcon = !!props.icon
  const fn = Modal[type]

  const okClick = () => {
    props.onOk?.()
    ModalInstance && ModalInstance.destroy()
  }

  const cancelClick = () => {
    props.onCancel?.()
    ModalInstance && ModalInstance.destroy()
  }

  const buttonMapping = {
    success: null,
    warn: (
      <StyledCustomModalFooter>
        <DDButton type="primary" className="modal-button-primary" onClick={() => okClick()}>
          {props.okText ? props.okText : '知道了'}
        </DDButton>
      </StyledCustomModalFooter>
    ),
    info: null,
    error: null,
    confirm: (
      <StyledCustomModalFooter>
        <DDGhostBtn className="ant-btn-default" onClick={() => cancelClick()}>
          {props.cancelText ? props.cancelText : '取消'}
        </DDGhostBtn>
        <DDButton type="primary" className="modal-button-primary" onClick={() => okClick()}>
          {props.okText ? props.okText : '知道了'}
        </DDButton>
      </StyledCustomModalFooter>
    ),
  }

  const ModalInstance = fn({
    ...props,
    title: undefined,
    icon: undefined,
    centered: true,
    content: (
      <StyledCustomModalContainer>
        {props.title && (
          <StyledCustomModalHeader style={{ paddingLeft: hasIcon ? 16 : 30 }}>
            {hasIcon && <div className="header-icon">{props.icon}</div>}
            {props.title}
          </StyledCustomModalHeader>
        )}
        <StyledCustomModalContent>{props.content}</StyledCustomModalContent>
        {buttonMapping[type]}
      </StyledCustomModalContainer>
    ),
  })
  return ModalInstance
}

const DDModal: FC<ModalProps> & { warn: ModalFunc; confirm: ModalFunc } = ({ children, ...props }) => {
  return (
    <StyledDDModal centered={true} {...props}>
      {children}
    </StyledDDModal>
  )
}

DDModal.warn = props => {
  return handleOpenModal(
    {
      ...props,
      className: `${props.className} dd-custom-modal dd-custom-modal-warn`,
      icon: iconMapping['warn'],
    },
    'warn',
  )
}

DDModal.confirm = props => {
  return handleOpenModal(
    {
      ...props,
      className: `${props.className} dd-custom-modal dd-custom-modal-confirm`,
      icon: iconMapping['warn'],
    },
    'confirm',
  )
}

const StyledCustomModalFooter = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid var(--modal-footer-divider-color);
  background: var(--modal-content-bg);
  margin: 0 20px;
  .ant-btn-default {
    height: 32px;
    margin-right: 10px;
  }
  .modal-button-primary {
    height: 32px;
  }
`

const StyledCustomModalContent = styled.div`
  background: var(--modal-content-bg);
  padding: 30px;
  color: var(--modal-content-default-text-color);
`

const StyledCustomModalHeader = styled.div`
  height: 50px;
  background: var(--modal-header-bg);
  border-radius: 4px 4px 0px 0px;
  display: flex;
  align-items: center;
  color: var(--modal-header-text-color);
  font-size: var(--fontsize-16);
  font-weight: 700;
  .header-icon {
    color: var(--modal-header-icon-color);
    font-size: var(--fontsize-18);
    margin-right: 12px;
  }
`

const StyledCustomModalContainer = styled.div`
  background: var(--modal-content-bg);
`

const StyledDDModal = styled(Modal)`
  .ant-modal-header {
    padding: 13px 20px;
    background: #121517;
    border: none;
    .ant-modal-title {
      font-size: 16px;
      color: #fff;
    }
  }
  .ant-modal-close-x {
    height: 41px;
    width: 41px;
    line-height: 41px;
    color: #84888b;
    font-size: 12px;
  }
  .ant-modal-body {
    background: #22262a;
    padding: 20px;
  }
`

export default observer(DDModal)
